import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Pencil, Trash2, X } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { formatCents, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import { SortableTh } from '@/components/SortableTh';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface PoPaymentRow {
  id: number;
  po_id: number;
  po_number: string;
  supplier_id: number;
  supplier_name: string;
  amount: number;
  date: string;
  notes: string | null;
  created_by: number | null;
  created_at: string;
}

// ─── Edit PO Payment Modal ─────────────────────────────────────────────────────

interface EditPoPaymentModalProps {
  payment: PoPaymentRow;
  onClose: () => void;
  onSaved: () => void;
}

function EditPoPaymentModal({ payment, onClose, onSaved }: EditPoPaymentModalProps) {
  const [amountStr, setAmountStr] = useState(String((payment.amount / 100).toFixed(2)));
  const [date, setDate] = useState(payment.date.slice(0, 10));
  const [notes, setNotes] = useState(payment.notes ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amount = Math.round((parseFloat(amountStr) || 0) * 100);
    if (amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/po-payments/${payment.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          amount,
          date,
          notes: notes.trim() || undefined,
        }),
      });
      toast.success('Payment updated');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Edit Payment</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="pp-amount">
                Amount ($) <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  $
                </span>
                <Input
                  id="pp-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className="pl-7 tabular-nums"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pp-date">Date</Label>
              <Input
                id="pp-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pp-notes">Notes (optional)</Label>
            <Input
              id="pp-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Cash payment"
              disabled={loading}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Delete Confirm Dialog ─────────────────────────────────────────────────────

interface DeleteDialogProps {
  payment: PoPaymentRow;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}

function DeleteDialog({ payment, onCancel, onConfirm, loading }: DeleteDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Delete Payment</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Delete the{' '}
            <span className="font-semibold text-foreground">{formatCents(payment.amount)}</span>{' '}
            payment on{' '}
            <span className="font-semibold text-foreground">{formatDate(payment.date)}</span> for{' '}
            <span className="font-semibold text-foreground">{payment.supplier_name}</span>? This
            cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function PoPaymentsPage() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [editingPayment, setEditingPayment] = useState<PoPaymentRow | undefined>(undefined);
  const [deletingPayment, setDeletingPayment] = useState<PoPaymentRow | undefined>(undefined);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    rows: payments,
    total,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    dir,
    toggleSort,
    isLoading,
  } = usePaginatedList<PoPaymentRow>({
    endpoint: '/api/po-payments',
    entityKey: 'payments',
    defaultSort: 'date',
    defaultDir: 'desc',
    extraParams: {
      from: from || undefined,
      to: to || undefined,
    },
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['po-payments'] });
    queryClient.invalidateQueries({ queryKey: ['supplier-ledger'] });
  }

  async function handleDelete() {
    if (!deletingPayment) return;
    setDeleteLoading(true);
    try {
      await apiFetch(`/api/po-payments/${deletingPayment.id}`, { method: 'DELETE' });
      toast.success('Payment deleted');
      setDeletingPayment(undefined);
      invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete payment';
      toast.error(msg);
      setDeletingPayment(undefined);
    } finally {
      setDeleteLoading(false);
    }
  }

  const colSpan = isAdmin ? 6 : 5;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Supplier Payments</h1>
          {!isLoading && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
        </div>
        <ExportButtons
          endpoint="/api/po-payments"
          params={{
            from: from || undefined,
            to: to || undefined,
          }}
        />
      </div>

      {/* Filters — one line: date range (wraps on small screens) */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Date range */}
        <div className="flex items-center gap-2 text-sm shrink-0">
          <span className="text-muted-foreground">From</span>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          <span className="text-muted-foreground">To</span>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          {(from || to) && (
            <button
              type="button"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableTh
                label="Date"
                sortKey="date"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28"
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Supplier</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground w-32 hidden sm:table-cell">
                PO #
              </th>
              <SortableTh
                label="Amount"
                sortKey="amount"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28 text-right"
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">
                Notes
              </th>
              {isAdmin && (
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && payments.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-muted-foreground">
                  No payments found.
                </td>
              </tr>
            )}
            {payments.map((payment) => (
              <tr key={payment.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 text-muted-foreground tabular-nums whitespace-nowrap">
                  {formatDate(payment.date)}
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={`/suppliers/${payment.supplier_id}`}
                    className="hover:underline underline-offset-4"
                  >
                    {payment.supplier_name}
                  </Link>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  <Link
                    to={`/purchase-orders/${payment.po_id}`}
                    className="font-mono text-xs hover:underline underline-offset-4 text-muted-foreground"
                  >
                    {payment.po_number}
                  </Link>
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-medium">
                  {formatCents(payment.amount)}
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                  {payment.notes ?? '—'}
                </td>
                {isAdmin && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setEditingPayment(payment)}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:border-destructive/50"
                        onClick={() => setDeletingPayment(payment)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Edit modal */}
      {editingPayment && (
        <EditPoPaymentModal
          payment={editingPayment}
          onClose={() => setEditingPayment(undefined)}
          onSaved={invalidate}
        />
      )}

      {/* Delete dialog */}
      {deletingPayment && (
        <DeleteDialog
          payment={deletingPayment}
          onCancel={() => setDeletingPayment(undefined)}
          onConfirm={handleDelete}
          loading={deleteLoading}
        />
      )}
    </div>
  );
}
